import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { useSubscriptionStatus } from '../../subscription';
import { Button, FormField, globalNotifications } from '../../ui';
import NotificationService from '../services/NotificationService';
import './NotificationCenter.css';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const MIN_MARGIN_FLOOR = 1.0;
const MIN_SPREAD_FLOOR = 5.0;
const DISABLED_THRESHOLD = 999.0;
const DEFAULT_MARGIN_PLACEHOLDER = '1.5';
const DEFAULT_SPREAD_PLACEHOLDER = '10.0';

const decodeThreshold = function(value, placeholder, enabledWhenNull) {
  if (value === null || value === undefined) {
    return { enabled: enabledWhenNull, value: placeholder };
  }
  if (Number(value) === DISABLED_THRESHOLD) {
    return { enabled: false, value: placeholder };
  }
  return { enabled: true, value: String(value) };
};

/**
 * NotificationCenter - Telegram notification settings in user profile
 * @returns {JSX.Element} Notification center section
 */
const NotificationCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isActive } = useSubscriptionStatus();
  const mountedRef = useRef(true);
  const saveFeedbackTimeoutRef = useRef(null);

  const [settings, setSettings] = useState(null);
  const [marginEnabled, setMarginEnabled] = useState(true);
  const [marginValue, setMarginValue] = useState('3.0');
  const [marginError, setMarginError] = useState('');
  const [spreadEnabled, setSpreadEnabled] = useState(false);
  const [spreadValue, setSpreadValue] = useState(DEFAULT_SPREAD_PLACEHOLDER);
  const [spreadError, setSpreadError] = useState('');
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);

  useEffect(function() {
    mountedRef.current = true;
    return function() {
      mountedRef.current = false;
      if (saveFeedbackTimeoutRef.current) {
        clearTimeout(saveFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const scheduleSaveFeedbackClear = useCallback(function() {
    if (saveFeedbackTimeoutRef.current) {
      clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = setTimeout(function() {
      setSaveFeedback(null);
    }, 6000);
  }, []);

  const showSaveSuccessFeedback = useCallback(function() {
    setSaveFeedback({
      type: 'success',
      message: t('profile.notifications.saved'),
    });
    scheduleSaveFeedbackClear();
    globalNotifications.success(t('profile.notifications.saved'));
  }, [scheduleSaveFeedbackClear, t]);

  const showSaveErrorFeedback = useCallback(function(message) {
    setSaveFeedback({
      type: 'error',
      message: message || t('profile.notifications.settings.saveErrorGeneric'),
    });
    scheduleSaveFeedbackClear();
  }, [scheduleSaveFeedbackClear, t]);

  const handlePaymentRequired = useCallback(function() {
    navigate('/subscription');
  }, [navigate]);

  const applySettings = useCallback(function(data) {
    if (!data) {
      return;
    }
    setSettings(data);
    setTelegramEnabled(Boolean(data.telegramEnabled));
    setQuietHoursEnabled(Boolean(data.quietHoursEnabled));
    const margin = decodeThreshold(data.minMarginAlert, DEFAULT_MARGIN_PLACEHOLDER, true);
    const spread = decodeThreshold(data.minSpreadAlert, DEFAULT_SPREAD_PLACEHOLDER, false);
    setMarginEnabled(margin.enabled);
    setMarginValue(margin.value);
    setSpreadEnabled(spread.enabled);
    setSpreadValue(spread.value);
  }, []);

  const loadSettings = useCallback(async function() {
    if (!isActive) {
      return;
    }

    setLoadingSettings(true);
    const { data, error } = await NotificationService.getSettings();
    if (!mountedRef.current) {
      return;
    }

    setLoadingSettings(false);

    if (error?.status === 402) {
      handlePaymentRequired();
      return;
    }

    if (error) {
      globalNotifications.error(error.message);
      return;
    }

    applySettings(data);
  }, [applySettings, handlePaymentRequired, isActive]);

  useEffect(function() {
    if (isActive) {
      loadSettings();
    }
  }, [isActive, loadSettings]);

  useEffect(function() {
    if (!isActive || !linkGenerated || settings?.connected) {
      return undefined;
    }

    const startedAt = Date.now();
    let intervalId = null;

    const pollConnection = async function() {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        if (mountedRef.current) {
          setLinkGenerated(false);
          setPollTimedOut(true);
        }
        if (intervalId !== null) {
          clearInterval(intervalId);
        }
        return;
      }

      const { data, error } = await NotificationService.getSettings();
      if (!mountedRef.current) {
        return;
      }

      if (error?.status === 402) {
        handlePaymentRequired();
        return;
      }

      if (data?.connected) {
        applySettings(data);
        setLinkGenerated(false);
        setPollTimedOut(false);
        globalNotifications.success(t('profile.notifications.connected'));
        if (intervalId !== null) {
          clearInterval(intervalId);
        }
      }
    };

    pollConnection();
    intervalId = setInterval(pollConnection, POLL_INTERVAL_MS);
    return function() {
      clearInterval(intervalId);
    };
  }, [
    applySettings,
    handlePaymentRequired,
    isActive,
    linkGenerated,
    settings?.connected,
    t,
  ]);

  const ensureActiveSubscription = useCallback(function() {
    if (!isActive) {
      handlePaymentRequired();
      return false;
    }
    return true;
  }, [handlePaymentRequired, isActive]);

  const handleConnect = async function() {
    if (!ensureActiveSubscription()) {
      return;
    }

    setLinkLoading(true);
    const { data, error } = await NotificationService.generateTelegramLink();
    if (!mountedRef.current) {
      return;
    }
    setLinkLoading(false);

    if (error?.status === 402) {
      handlePaymentRequired();
      return;
    }

    if (error) {
      globalNotifications.error(error.message);
      return;
    }

    const deepLink = data?.deepLink;
    if (!deepLink) {
      globalNotifications.error(t('profile.notifications.linkError'));
      return;
    }

    setLinkGenerated(true);
    setPollTimedOut(false);
    window.open(deepLink, '_blank', 'noopener,noreferrer');
  };

  const handleTelegramEnabledChange = function(event) {
    setTelegramEnabled(event.target.checked);
  };

  const handleQuietHoursEnabledChange = function(event) {
    setQuietHoursEnabled(event.target.checked);
  };

  const handleSaveFilters = async function() {
    if (!ensureActiveSubscription()) {
      return;
    }

    let hasError = false;
    let minMarginAlert = DISABLED_THRESHOLD;
    let minSpreadAlert = DISABLED_THRESHOLD;
    const validationMessages = [];

    if (marginEnabled) {
      const marginValidation = NotificationService.validateMinMargin(marginValue);
      if (!marginValidation.valid) {
        setMarginError(t('profile.notifications.settings.minFloorError'));
        validationMessages.push(t('profile.notifications.settings.minFloorError'));
        hasError = true;
      } else {
        setMarginError('');
        minMarginAlert = marginValidation.value;
      }
    } else {
      setMarginError('');
    }

    if (spreadEnabled) {
      const spreadValidation = NotificationService.validateMinSpread(spreadValue);
      if (!spreadValidation.valid) {
        setSpreadError(t('profile.notifications.settings.minSpreadFloorError'));
        validationMessages.push(t('profile.notifications.settings.minSpreadFloorError'));
        hasError = true;
      } else {
        setSpreadError('');
        minSpreadAlert = spreadValidation.value;
      }
    } else {
      setSpreadError('');
    }

    if (hasError) {
      showSaveErrorFeedback(validationMessages.join(' '));
      return;
    }

    setSaveFeedback(null);
    setSaveLoading(true);

    try {
      const { data, error } = await NotificationService.updateSettings({
        telegramEnabled,
        quietHoursEnabled,
        minMarginAlert,
        minSpreadAlert,
      });

      if (error?.status === 402) {
        handlePaymentRequired();
        return;
      }

      if (error) {
        showSaveErrorFeedback(error.message);
        globalNotifications.error(error.message);
        return;
      }

      applySettings(data);
      showSaveSuccessFeedback();
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDisconnect = async function() {
    if (!ensureActiveSubscription()) {
      return;
    }

    setDisconnectLoading(true);
    const { data, error } = await NotificationService.disconnect();

    if (!mountedRef.current) {
      return;
    }
    setDisconnectLoading(false);

    if (error?.status === 402) {
      handlePaymentRequired();
      return;
    }

    if (error) {
      globalNotifications.error(error.message);
      return;
    }

    applySettings(data);
    setLinkGenerated(false);
    globalNotifications.success(t('profile.notifications.disconnected'));
  };

  const handleMarginChange = function(event) {
    setMarginValue(event.target.value);
    if (marginError) {
      setMarginError('');
    }
  };

  const handleMarginEnabledChange = function(event) {
    setMarginEnabled(event.target.checked);
    if (marginError) {
      setMarginError('');
    }
  };

  const handleSpreadChange = function(event) {
    setSpreadValue(event.target.value);
    if (spreadError) {
      setSpreadError('');
    }
  };

  const handleSpreadEnabledChange = function(event) {
    setSpreadEnabled(event.target.checked);
    if (spreadError) {
      setSpreadError('');
    }
  };

  const connected = Boolean(settings?.connected);
  const isSaveSuccess = saveFeedback?.type === 'success';
  const displayMarginEnabled = telegramEnabled && marginEnabled;
  const displaySpreadEnabled = telegramEnabled && spreadEnabled;

  const renderSaveFeedback = function() {
    if (!saveFeedback) {
      return null;
    }

    return (
      <div
        className={`notification-center__feedback notification-center__feedback--${saveFeedback.type}`}
        role="status"
        aria-live="polite"
        data-testid="notification-settings-feedback"
      >
        <span className="notification-center__feedback-icon" aria-hidden="true">
          {saveFeedback.type === 'success' ? '✓' : '!'}
        </span>
        <span>{saveFeedback.message}</span>
      </div>
    );
  };

  return (
    <div className="notification-center" data-testid="notification-center">
      <h2 className="profile-section-title">{t('profile.notifications.title')}</h2>
      <p className="notification-center__subtitle">{t('profile.notifications.subtitle')}</p>

      {!isActive && (
        <p className="notification-center__gate">{t('profile.notifications.subscriptionRequired')}</p>
      )}

      {isActive && loadingSettings && !settings && (
        <div className="notification-center__loading">
          <div className="loading-spinner" />
          <p>{t('profile.notifications.loading')}</p>
        </div>
      )}

      {isActive && settings && !connected && (
        <div className="notification-center__step">
          <h3 className="notification-center__heading">
            {t('profile.notifications.connect.heading')}
          </h3>
          <p className="notification-center__text">{t('profile.notifications.connect.text')}</p>
          <Button
            variant="primary"
            onClick={handleConnect}
            loading={linkLoading}
            testId="telegram-connect-button"
          >
            {t('profile.notifications.connect.button')}
          </Button>
          <p className="notification-center__helper">{t('profile.notifications.connect.helper')}</p>
          {linkGenerated && (
            <p className="notification-center__polling">{t('profile.notifications.connect.waiting')}</p>
          )}
          {pollTimedOut && (
            <p className="notification-center__timeout">{t('profile.notifications.connect.pollTimeout')}</p>
          )}
        </div>
      )}

      {isActive && settings && connected && (
        <div className="notification-center__step">
          {renderSaveFeedback()}

          <h3 className="notification-center__heading">
            {t('profile.notifications.settings.generalHeading')}
          </h3>

          <label className="notification-center__toggle">
            <input
              type="checkbox"
              checked={telegramEnabled}
              onChange={handleTelegramEnabledChange}
              disabled={saveLoading}
              data-testid="telegram-enabled-toggle"
            />
            <span>{t('profile.notifications.settings.enabledToggle')}</span>
          </label>

          {!telegramEnabled && (
            <p className="notification-center__paused">{t('profile.notifications.settings.paused')}</p>
          )}

          <label className="notification-center__toggle">
            <input
              type="checkbox"
              checked={quietHoursEnabled}
              onChange={handleQuietHoursEnabledChange}
              disabled={saveLoading}
              data-testid="quiet-hours-toggle"
            />
            <span>{t('profile.notifications.settings.quietHoursToggle')}</span>
          </label>
          <p className="notification-center__helper">
            {t('profile.notifications.settings.quietHoursHelper')}
          </p>

          <div className={`notification-center__filter-group ${!telegramEnabled ? 'notification-center__filter-group--inactive' : ''}`}>
            <h4 className="notification-center__subheading">
              {t('profile.notifications.settings.arbitrageFilterHeading')}
            </h4>
            <label className="notification-center__toggle">
              <input
                type="checkbox"
                checked={displayMarginEnabled}
                onChange={handleMarginEnabledChange}
                disabled={!telegramEnabled || saveLoading}
                data-testid="margin-filter-toggle"
              />
              <span>{t('profile.notifications.settings.arbitrageFilterToggle')}</span>
            </label>
            {displayMarginEnabled && (
              <div className="notification-center__margin">
                <p className="notification-center__label">
                  {t('profile.notifications.settings.marginLabel')}
                </p>
                <FormField
                  type="number"
                  name="minMarginAlert"
                  value={marginValue}
                  onChange={handleMarginChange}
                  min={MIN_MARGIN_FLOOR}
                  step="0.1"
                  placeholder={t('profile.notifications.settings.marginPlaceholder')}
                  error={marginError}
                  endIcon={<span className="notification-center__suffix">%</span>}
                  testId="min-margin-alert-input"
                />
              </div>
            )}
          </div>

          <div className={`notification-center__filter-group ${!telegramEnabled ? 'notification-center__filter-group--inactive' : ''}`}>
            <h4 className="notification-center__subheading">
              {t('profile.notifications.settings.bestOddsFilterHeading')}
            </h4>
            <label className="notification-center__toggle">
              <input
                type="checkbox"
                checked={displaySpreadEnabled}
                onChange={handleSpreadEnabledChange}
                disabled={!telegramEnabled || saveLoading}
                data-testid="spread-filter-toggle"
              />
              <span>{t('profile.notifications.settings.bestOddsFilterToggle')}</span>
            </label>
            {displaySpreadEnabled && (
              <div className="notification-center__margin">
                <p className="notification-center__label">
                  {t('profile.notifications.settings.spreadLabel')}
                </p>
                <FormField
                  type="number"
                  name="minSpreadAlert"
                  value={spreadValue}
                  onChange={handleSpreadChange}
                  min={MIN_SPREAD_FLOOR}
                  step="0.1"
                  placeholder={t('profile.notifications.settings.spreadPlaceholder')}
                  error={spreadError}
                  endIcon={<span className="notification-center__suffix">%</span>}
                  testId="min-spread-alert-input"
                />
              </div>
            )}
          </div>

          <div className="notification-center__anti-spam">
            <p className="notification-center__anti-spam-title">
              {t('profile.notifications.settings.antiSpam.title')}
            </p>
            <p className="notification-center__anti-spam-intro">
              {t('profile.notifications.settings.antiSpam.intro')}
            </p>
            <ul className="notification-center__anti-spam-list">
              <li>{t('profile.notifications.settings.antiSpam.arbitrage')}</li>
              <li>{t('profile.notifications.settings.antiSpam.bestOdds')}</li>
            </ul>
          </div>

          <div className="notification-center__actions">
            <Button
              variant="primary"
              onClick={handleSaveFilters}
              loading={saveLoading}
              className={isSaveSuccess ? 'notification-center__save-button--success' : ''}
              testId="save-notification-settings-button"
            >
              {isSaveSuccess
                ? t('profile.notifications.settings.savedButton')
                : t('profile.notifications.settings.save')}
            </Button>
            <Button
              variant="secondary"
              onClick={handleDisconnect}
              loading={disconnectLoading}
              testId="telegram-disconnect-button"
            >
              {t('profile.notifications.settings.disconnect')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
